import React, { type ChangeEvent, useEffect } from "react";
import { Form } from "semantic-ui-react";
import cn from "classnames";
import get from "lodash/get";

import ValidationErrorPrompt from "./ValidationErrorPrompt";
import FieldLabel from "./FieldLabel";

import "./validatedField.scss";
import { HiddenError } from "./constants";
import { type ErrorPosition, ErrorPositions } from "../../enums/errorPositions";

interface Tooltip {
  info: React.ReactNode;
  width?: number;
  hoverable?: boolean;
}

export interface ValidatedFieldProps {
  label?: string;
  propertyName: string;
  children?: React.ReactNode;

  tooltip?: Tooltip;
  markAsRequired?: boolean;
  value?: string | number | boolean | object | [any] | null;
  placeholder?: string;
  errorPosition?: ErrorPositions | ErrorPosition;
  errors?: any;
  touched?: any;
  handleChange?: (e: ChangeEvent<any>, data?: { value: string }) => void;
  handleBlur?: (e: ChangeEvent<any>) => void;
  isFormValid?: boolean;
  onIsFormValidChange?: (isFormValid: boolean) => void;
  pushdown?: boolean;
  withoutErrorMessage?: boolean;
  inline?: boolean;
  className?: string;
  errorClassName?: string;
  fluid?: boolean;
}

export default function ValidatedField(props: ValidatedFieldProps) {
  const {
    label,
    tooltip,
    propertyName,
    errors,
    touched,
    markAsRequired,
    pushdown,
    onIsFormValidChange,
    fluid,
    errorPosition = ErrorPositions.right,
    withoutErrorMessage = false,
    isFormValid = true,
    inline = false,
    className,
  } = props;

  useEffect(() => {
    onIsFormValidChange?.(isFormValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormValid]);
  const errorMessage = get(errors, propertyName);
  const isInErrorState = errorMessage && isPropertyTouched(touched, propertyName);

  return (
    <>
      {label && (
        <FieldLabel label={label} tooltip={tooltip} markAsRequired={markAsRequired} isInErrorState={isInErrorState} />
      )}
      <Form.Field
        error={isInErrorState}
        className={cn("validated-field", errorPosition, className, {
          pushdown,
          inline,
          fluid,
        })}
      >
        {props.children}
        {!withoutErrorMessage && isInErrorState && errorMessage !== HiddenError && (
          <ValidationErrorPrompt
            errorPosition={errorPosition}
            text={errorMessage}
            errorClassName={props.errorClassName}
          />
        )}
      </Form.Field>
    </>
  );
}

function isPropertyTouched(touched: boolean, propertyName: string) {
  let value = get(touched, propertyName);
  // To work around the issue with multivalue field validation:
  // https://github.com/jaredpalmer/formik/issues/793
  if (value instanceof Array && value.length === 0) {
    return true;
  }
  // Otherwise should work fine
  return !!value;
}
